<template lang="pug">
.d-flex.flex-wrap.align-center.gap-2
  .d-flex.flex-wrap.align-center.gap-2.flex-grow-1
    filter-invoice-number(ref="invoiceNumber" :filter.sync="invoiceNumberFilter")
    filter-panel(ref="panel" :filter.sync="panelFilter")
    filter-patient-name(ref="patientName" :filter.sync="patientNameFilter")
    filter-doctor(ref="doctor" :filter.sync="doctorFilter")
    filter-visit-type(ref="visitType" :filter.sync="visitTypeFilter")
    filter-mc-days(ref="mcDays" :filter.sync="mcDaysFilter")
    filter-diagnosis(ref="diagnosis" :filter.sync="diagnosisFilter")
    filter-treatment(ref="treatment" :filter.sync="treatmentFilter")
    filter-visit-date(ref="visitDate" :filter.sync="visitDateFilter")
    filter-due-date(ref="dueDate" :filter.sync="dueDateFilter")
  yz-btn(:label="$t('reset_filters')" text, underline, @click="resetFilters" :disabled="!hasActiveFilters")
</template>

<script>
import FilterInvoiceNumber from '@/components/clinic/panel/claims/filters/FilterInvoiceNumber.vue'
import FilterPanel from '@/components/clinic/panel/claims/filters/FilterPanel.vue'
import FilterPatientName from '@/components/clinic/panel/claims/filters/FilterPatientName.vue'
import FilterDoctor from '@/components/clinic/panel/claims/filters/FilterDoctor.vue'
import FilterVisitType from '@/components/clinic/panel/claims/filters/FilterVisitType.vue'
import FilterMcDays from '@/components/clinic/panel/claims/filters/FilterMcDays.vue'
import FilterDiagnosis from '@/components/clinic/panel/claims/filters/FilterDiagnosis.vue'
import FilterTreatment from '@/components/clinic/panel/claims/filters/FilterTreatment.vue'
import FilterVisitDate from '@/components/clinic/panel/claims/filters/FilterVisitDate.vue'
import FilterDueDate from '@/components/clinic/panel/claims/filters/FilterDueDate.vue'

export default {
  components: { FilterInvoiceNumber, FilterPanel, FilterPatientName, FilterDoctor, FilterVisitType, FilterMcDays, FilterDiagnosis, FilterTreatment, FilterVisitDate, FilterDueDate },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    invoiceNumberFilter: {},
    panelFilter: {},
    patientNameFilter: {},
    doctorFilter: {},
    visitTypeFilter: {},
    mcDaysFilter: {},
    diagnosisFilter: {},
    treatmentFilter: {},
    visitDateFilter: {},
    dueDateFilter: {},
    isInitializing: false
  }),
  computed: {
    allFilters() {
      const filters = {}

      // Collect all active filters
      if (this.invoiceNumberFilter.key) {
        filters[this.invoiceNumberFilter.key] = this.invoiceNumberFilter.value
      }
      if (this.panelFilter.key) {
        filters[this.panelFilter.key] = this.panelFilter.value
      }
      if (this.patientNameFilter.key) {
        filters[this.patientNameFilter.key] = this.patientNameFilter.value
      }
      if (this.doctorFilter.key) {
        filters[this.doctorFilter.key] = this.doctorFilter.value
      }
      if (this.visitTypeFilter.key) {
        filters[this.visitTypeFilter.key] = this.visitTypeFilter.value
      }
      if (this.mcDaysFilter.key) {
        Object.assign(filters, this.buildFilterParams(this.mcDaysFilter))
      }
      if (this.diagnosisFilter.key) {
        filters[this.diagnosisFilter.key] = this.diagnosisFilter.value
      }
      if (this.treatmentFilter.key) {
        filters[this.treatmentFilter.key] = this.treatmentFilter.value
      }
      if (this.visitDateFilter.key) {
        Object.assign(filters, this.buildFilterParams(this.visitDateFilter))
      }
      if (this.dueDateFilter.key) {
        Object.assign(filters, this.buildFilterParams(this.dueDateFilter))
      }

      return filters
    },
    hasActiveFilters() {
      return Object.keys(this.allFilters).length > 0
    }
  },
  watch: {
    allFilters: {
      handler(newValue) {
        // Don't emit during initialization to avoid circular updates
        if (!this.isInitializing) {
          this.$emit('update:filters', newValue)
          // Also emit serialized version for URL
          const serialized = this.serializeFilters(newValue)
          this.$emit('filters-serialized', serialized)
        }
      },
      deep: true
    },
    filters: {
      handler(newValue) {
        this.initializeFiltersFromProps(newValue)
      },
      immediate: true
    }
  },
  methods: {
    buildFilterParams(filter) {
      // Handle filters that might have multiple keys (like between ranges)
      if (filter.params) {
        return filter.params
      }
      return { [filter.key]: filter.value }
    },
    serializeFilters(filters) {
      // Serialize filters to base64 encoded string
      if (!filters || Object.keys(filters).length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        // Use encodeURIComponent to handle unicode characters properly
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    deserializeFilters(base64String) {
      // Deserialize base64 encoded string back to filters object
      if (!base64String) {
        return {}
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        // Show toast message to user
        this.$store.dispatch('showMessage', {
          message: this.$t('filter_parameters_corrupted'),
          color: 'warning'
        })
        return {}
      }
    },
    async initializeFiltersFromProps(filters) {
      // Initialize filters from props if provided (e.g., from URL params)
      if (!filters || Object.keys(filters).length === 0) return

      // Set flag to prevent circular updates
      this.isInitializing = true

      try {
        // If filters is a base64 string, deserialize it first
        let filterParams = filters
        if (typeof filters === 'string') {
          filterParams = this.deserializeFilters(filters)
        }

        if (!filterParams || Object.keys(filterParams).length === 0) return

        // Call restoreFromParams on each filter component
        const restorePromises = []

        // Map filter params to corresponding filter components
        if (filterParams.amount !== undefined || filterParams.amount__gt !== undefined || filterParams.amount__lt !== undefined || filterParams.amount__ne !== undefined || (filterParams.amount__gte !== undefined && filterParams.amount__lte !== undefined)) {
          if (this.$refs.amount) restorePromises.push(this.$refs.amount.restoreFromParams(filterParams))
        }
        if (filterParams.rid) {
          if (this.$refs.invoiceNumber) restorePromises.push(this.$refs.invoiceNumber.restoreFromParams(filterParams))
        }
        if (filterParams.panel) {
          if (this.$refs.panel) restorePromises.push(this.$refs.panel.restoreFromParams(filterParams))
        }
        if (filterParams.patient_name || filterParams.patient_name__ne || filterParams.patient_name__contains || filterParams.patient_name__exclude || filterParams.patient_name__startswith || filterParams.patient_name__endswidth) {
          if (this.$refs.patientName) restorePromises.push(this.$refs.patientName.restoreFromParams(filterParams))
        }
        if (filterParams.providers) {
          if (this.$refs.doctor) restorePromises.push(this.$refs.doctor.restoreFromParams(filterParams))
        }
        if (filterParams.visit_types) {
          if (this.$refs.visitType) restorePromises.push(this.$refs.visitType.restoreFromParams(filterParams))
        }
        if (filterParams.mc_days !== undefined || filterParams.mc_days__gt !== undefined || filterParams.mc_days__lt !== undefined || filterParams.mc_days__ne !== undefined) {
          if (this.$refs.mcDays) restorePromises.push(this.$refs.mcDays.restoreFromParams(filterParams))
        }
        if (filterParams.diagnosis) {
          if (this.$refs.diagnosis) restorePromises.push(this.$refs.diagnosis.restoreFromParams(filterParams))
        }
        if (filterParams.treatment_products) {
          if (this.$refs.treatment) restorePromises.push(this.$refs.treatment.restoreFromParams(filterParams))
        }
        if (filterParams.visit_date || filterParams.visit_date__gt || filterParams.visit_date__gte || filterParams.visit_date__lt || filterParams.visit_date__lte) {
          if (this.$refs.visitDate) restorePromises.push(this.$refs.visitDate.restoreFromParams(filterParams))
        }
        if (filterParams.due_date || filterParams.due_date__gt || filterParams.due_date__gte || filterParams.due_date__lt || filterParams.due_date__lte) {
          if (this.$refs.dueDate) restorePromises.push(this.$refs.dueDate.restoreFromParams(filterParams))
        }

        await Promise.all(restorePromises)
      } finally {
        // Reset flag after initialization
        this.isInitializing = false
      }
    },
    resetFilters() {
      this.invoiceNumberFilter = {}
      this.panelFilter = {}
      this.patientNameFilter = {}
      this.doctorFilter = {}
      this.visitTypeFilter = {}
      this.mcDaysFilter = {}
      this.diagnosisFilter = {}
      this.treatmentFilter = {}
      this.visitDateFilter = {}
      this.dueDateFilter = {}

      // Reset all child filter components
      Object.keys(this.$refs).forEach(key => {
        if (this.$refs[key] && typeof this.$refs[key].reset === 'function') {
          this.$refs[key].reset()
        }
      })
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
